import { coerceWidgetData, deriveAlt } from "@docit/doc-widgets";
import type {
  JSONContent,
  MarkdownRendererHelpers,
  MarkdownToken,
  RenderContext,
} from "@tiptap/core";
import { Extension } from "@tiptap/core";
import {
  type InstructionStatus,
  parseInstructionMentions,
} from "~/features/documents/editor/extensions/instructionBlock";
import { parseMentionHref } from "~/features/documents/editor/extensions/mention";

/**
 * The markdown PARSE rules for the owner's block nodes — deliberately separate from the nodes.
 *
 * Why they cannot live on the nodes themselves: the markdown manager tries a token's handlers
 * in **extension-priority order** and takes the first that claims the token, so a widget node
 * at default priority would always lose `code` to the ordinary code block and a conversation
 * node would always lose `paragraph` to the paragraph — which is exactly the upstream defect
 * (a `parseMarkdown` that never ran, so only the server ever turned a fence into a node).
 *
 * But raising a NODE's priority also reorders the schema, and the first block node in a schema
 * is the document's DEFAULT block — a `widget` at priority 1100 makes every empty document and
 * every trailing block a widget. Measured, not theorised: the round-trip test caught it.
 *
 * So the parse rules are schema-free `Extension`s at high priority, and the nodes keep their
 * ordinary priority. Rendering stays on the nodes, where it keys on the extension NAME and
 * has no ordering contest at all.
 *
 * Every rule DECLINES a token that is not its own, so ordinary fences and ordinary paragraphs
 * reach the ordinary handlers untouched.
 *
 * ── One consequence that is not optional ────────────────────────────────────────────────
 * The manager looks a RENDERER up by node type in the same registry it looks a PARSER up by
 * token type. For a fence that is harmless — the token is `code` and the node is `codeBlock`,
 * two different keys. For a paragraph the token name and the node name are the SAME string, so
 * whoever claims the `paragraph` token also becomes the paragraph's renderer. Being first is
 * the only way to parse before the built-in, so `BlockLinkRule` owns paragraph rendering too
 * and reproduces the built-in behaviour exactly. Found by the round-trip test, which caught a
 * list serialising as `- \n- \n` with its text silently gone.
 */

/** What the built-in paragraph renderer emits to keep a deliberate blank line. */
const EMPTY_PARAGRAPH_MARKDOWN = "&nbsp;";

/** `conversation://<id>` — the whole state of an embedded conversation. */
const CONVERSATION_HREF = /^conversation:\/\/([^/?#]+)$/;
/** `instruction://<id>?…` — an instruction that HAS run, state in the query string. */
const INSTRUCTION_HREF = /^instruction:\/\/([^/?#]+)(?:\?(.*))?$/;
/** A bare bracketed paragraph — an instruction that never ran. */
const INSTRUCTION_PATTERN = /^\[([^[\]]+)\]$/;

type LinkToken = { type?: string; href?: string; text?: string; raw?: string };

/**
 * The single link in a paragraph, or null.
 *
 * "Alone in its paragraph" is strict on purpose: a sentence that happens to contain such a
 * link is prose, and swallowing the paragraph would delete the words around it.
 */
function soleLink(token: MarkdownToken): LinkToken | null {
  const children = ((token.tokens ?? []) as LinkToken[]).filter(
    (child) => !(child.type === "text" && (child.raw ?? "").trim().length === 0),
  );
  const only = children.length === 1 ? children[0] : undefined;
  return only && only.type === "link" && typeof only.href === "string" ? only : null;
}

function isJsonObject(text: string): boolean {
  try {
    const parsed = JSON.parse(text);
    return Boolean(parsed) && typeof parsed === "object" && !Array.isArray(parsed);
  } catch {
    return false;
  }
}

function altOf(text: string): string {
  try {
    const data = coerceWidgetData(JSON.parse(text));
    return data ? deriveAlt(data as never) : "";
  } catch {
    return "";
  }
}

/**
 * ```widget and ```chart fences → nodes.
 *
 * The fence BODY becomes the `data` attr verbatim — never re-serialised — which is what makes
 * the round trip byte-identical and keeps a `value: null` hole a hole rather than a zero.
 */
export const WidgetFenceRule = Extension.create({
  name: "docitWidgetFenceRule",
  priority: 1100,
  markdownTokenName: "code",
  parseMarkdown(token) {
    const lang = String((token as { lang?: unknown }).lang ?? "").trim();
    if (lang !== "widget" && lang !== "chart") {
      return null as never;
    }
    const text = String(token.text ?? "");
    // A `widget`-labelled fence whose body is not an object is a code block that happens to be
    // labelled that way. Claiming it would silently eat somebody's example.
    if (!isJsonObject(text)) {
      return null as never;
    }
    return { type: lang, attrs: { data: text, alt: altOf(text) } };
  },
});

/**
 * Conversation and instruction paragraphs → nodes.
 *
 * One rule for both because they compete for the same `paragraph` token, and a single handler
 * that decides between them cannot be ordered wrongly against itself.
 */
export const BlockLinkRule = Extension.create({
  name: "docitBlockLinkRule",
  priority: 1100,
  markdownTokenName: "paragraph",

  /**
   * Paragraph rendering, because claiming the `paragraph` token also claims its renderer (see
   * the note above). Identical to the built-in: children, or — for an empty paragraph that
   * follows another empty one — the marker that keeps a deliberate blank line from collapsing.
   */
  renderMarkdown(node: JSONContent, h: MarkdownRendererHelpers, ctx: RenderContext) {
    if (!node) {
      return "";
    }
    const content = Array.isArray(node.content) ? node.content : [];
    if (content.length === 0) {
      const previous = ctx?.previousNode;
      const previousContent = Array.isArray(previous?.content) ? previous.content : [];
      const previousWasEmptyParagraph =
        previous?.type === "paragraph" && previousContent.length === 0;
      return previousWasEmptyParagraph ? EMPTY_PARAGRAPH_MARKDOWN : "";
    }
    return h.renderChildren(content as never);
  },

  parseMarkdown(token) {
    const link = soleLink(token);

    if (link?.href) {
      const conversation = CONVERSATION_HREF.exec(link.href);
      if (conversation?.[1]) {
        return {
          type: "conversation",
          attrs: {
            conversationId: conversation[1],
            title: String(link.text ?? ""),
            // JSON strings, never arrays — a Yjs attr compared by identity is rewritten whole
            // on every transaction.
            participantUserIds: "[]",
            participantThinkerIds: "[]",
          },
        };
      }

      const instruction = INSTRUCTION_HREF.exec(link.href);
      if (instruction?.[1]) {
        const params = new URLSearchParams(instruction[2] ?? "");
        return {
          type: "instruction_block",
          attrs: {
            instruction: params.get("instruction") ?? String(link.text ?? ""),
            targetThinkerIds: params.get("targetThinkerIds") ?? "[]",
            reviewRequested: params.get("reviewRequested") === "true",
            status: (params.get("status") ?? "complete") as InstructionStatus,
            conversationId: instruction[1],
            resultText: params.get("resultText"),
            errorMessage: params.get("errorMessage"),
          },
        };
      }
    }

    // The bare bracketed form must be the paragraph's ENTIRE content, so a sentence that
    // contains brackets stays a sentence.
    const raw = String(token.raw ?? token.text ?? "").trim();
    const bare = INSTRUCTION_PATTERN.exec(raw);
    if (!bare?.[1]) {
      return null as never;
    }
    const parsed = parseInstructionMentions(bare[1]);
    return {
      type: "instruction_block",
      attrs: {
        instruction: parsed.instruction,
        targetThinkerIds: JSON.stringify(parsed.thinkerMentions),
        reviewRequested: parsed.reviewRequested,
        status: "pending" as InstructionStatus,
        conversationId: null,
        resultText: null,
        errorMessage: null,
      },
    };
  },
});

/**
 * `@[Label](mention://<mentionId>/<type>/<modelId>)` → a mention node.
 *
 * Inline token handlers get NO fall-through — the manager takes the first handler for a token
 * type and never tries another — so claiming `link` means owning every link. This rule
 * therefore delegates: a mention href becomes a mention node, and everything else is rebuilt
 * as the ordinary link mark it would have been. The round-trip test asserts an ordinary link
 * still survives, because "mentions work and every other link vanished" is the exact failure
 * this shape can produce.
 */
export const MentionLinkRule = Extension.create({
  name: "docitMentionLinkRule",
  priority: 1100,
  markdownTokenName: "link",
  parseMarkdown(token, helpers) {
    const href = String((token as { href?: unknown }).href ?? "");
    const label = String(token.text ?? "");

    const mention = parseMentionHref(href, label);
    if (mention) {
      return { type: "mention", attrs: mention };
    }

    // Not a mention — reproduce the ordinary link mark rather than swallowing the token.
    const title = (token as { title?: unknown }).title;
    return helpers.applyMark("link", helpers.parseInline(token.tokens ?? []), {
      href,
      ...(typeof title === "string" && title ? { title } : {}),
    });
  },
});

/** Every rule, in the order a host should register them. */
export const DocitMarkdownRules = [WidgetFenceRule, BlockLinkRule, MentionLinkRule];
